/**
 * ChatBot Context
 * Provides global state management for the chatbot widget
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useSelector, useStore } from 'react-redux';
import { ChatBotState, ResolvedChatbotInit, EndChatFlowState, SurveyState } from '../types';
import { RootState } from '../../../reducers';
import { useChatMessageFlow } from './hooks/useChatMessageFlow';
import { useChatLifecycle } from './hooks/useChatLifecycle';
import { useEndChatFlow } from './hooks/useEndChatFlow';

interface ChatBotContextType {
  state: ChatBotState;
  isChatbotEnabled: boolean;
  isInitLoaded: boolean;
  assistantName: string;
  chatWindowTitle: string;
  labels: ResolvedChatbotInit['labels'];
  locale: string | undefined;
  endChatFlowState: EndChatFlowState;
  surveyState: SurveyState;
  sendMessage: (message: string) => Promise<boolean>;
  cancelOngoingRequest: () => void;
  retryLastMessage: () => Promise<boolean>;
  dismissMessage: (messageId: string) => void;
  toggleChat: () => void;
  clearChat: () => void;
  markAsRead: () => void;
  initiateEndChat: () => void;
  cancelEndChat: () => void;
  closeThankYou: () => void;
  confirmEndChat: () => void;
  submitSurvey: (scores: Record<number, number>, comments: string) => Promise<string | null>;
  skipSurvey: () => Promise<void>;
  openInlineFeedbackSurvey: () => Promise<void>;
}

const ChatBotContext = createContext<ChatBotContextType | undefined>(undefined);


export const ChatBotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authLocale = useSelector((state: RootState) => state.auth?.locale);
  const [surveyState, setSurveyState] = useState<SurveyState>({
    feedbackSurveyID: null,
    questions: [],
    scores: {},
    comments: '',
  });
  const {
    state,
    setState,
    initState,
    isInitLoaded,
    isSessionReadyRef,
    resetSession,
  } = useChatLifecycle({
    authLocale,
    setSurveyState,
  });


  const store = useStore<RootState>();
  const { sendMessage, cancelOngoingRequest } = useChatMessageFlow({
    labels: {
      errorFallbackMessage: initState.labels.errorFallbackMessage,
      unableToConnectBody: initState.labels.unableToConnectBody,
    },
    getReduxState: store.getState,
    setState,
    isSessionReadyRef,
  });
  const {
    endChatFlowState,
    initiateEndChat,
    cancelEndChat,
    closeThankYou,
    confirmEndChat,
    openInlineFeedbackSurvey,
    submitSurvey,
    skipSurvey,
  } = useEndChatFlow({
    isSessionReadyRef,
    resetSession,
    surveyState,
    setSurveyState,
  });

  const retryLastMessage = useCallback(async (): Promise<boolean> => {
    const lastUserMessage = [...state.messages].reverse().find(msg => msg.role === 'user');
    if (!lastUserMessage) {
      return false;
    }
    return sendMessage(lastUserMessage.content, lastUserMessage.id);
  }, [sendMessage, state.messages]);

  const dismissMessage = useCallback((messageId: string) => {
    setState(prev => ({
      ...prev,
      messages: prev.messages.filter(msg => msg.id !== messageId),
    }));
  }, [setState]);

  const toggleChat = useCallback(() => {
    setState(prev => ({
      ...prev,
      isOpen: !prev.isOpen,
      unreadCount: !prev.isOpen ? 0 : prev.unreadCount, // Clear unread when opening
    }));
  }, [setState]);

  const clearChat = useCallback(() => {
    resetSession();
  }, [resetSession]);

  const markAsRead = useCallback(() => {
    setState(prev => ({ ...prev, unreadCount: 0 }));
  }, [setState]);

  const value: ChatBotContextType = {
    state,
    isChatbotEnabled: initState.isEnabled,
    isInitLoaded,
    assistantName: initState.assistantName,
    chatWindowTitle: initState.chatWindowTitle,
    labels: initState.labels,
    locale: authLocale,
    endChatFlowState,
    surveyState,
    sendMessage,
    cancelOngoingRequest,
    retryLastMessage,
    dismissMessage,
    toggleChat,
    clearChat,
    markAsRead,
    initiateEndChat,
    cancelEndChat,
    closeThankYou,
    confirmEndChat,
    submitSurvey,
    skipSurvey,
    openInlineFeedbackSurvey,
  };

  return <ChatBotContext.Provider value={value}>{children}</ChatBotContext.Provider>;
};

export const useChatBotContext = (): ChatBotContextType => {
  const context = useContext(ChatBotContext);
  if (!context) {
    throw new Error('useChatBotContext must be used within ChatBotProvider');
  }
  return context;
};
